/** Number formatting shared by the dashboard and the social posts. */

export function fmtEth(x: number, digits = 4): string {
  if (!Number.isFinite(x)) return 'n/a';
  const abs = Math.abs(x);
  if (abs === 0) return '0';
  if (abs >= 1000) return compact(x, 1);
  if (abs >= 1) return x.toFixed(Math.min(digits, 3));
  if (abs >= 0.0001) return x.toFixed(digits);
  return x.toExponential(2);
}

export function compact(x: number, digits = 2): string {
  if (!Number.isFinite(x)) return 'n/a';
  const abs = Math.abs(x);
  const units: [number, string][] = [
    [1e12, 'T'],
    [1e9, 'B'],
    [1e6, 'M'],
    [1e3, 'K'],
  ];
  for (const [v, s] of units) if (abs >= v) return `${(x / v).toFixed(digits)}${s}`;
  return abs >= 100 ? x.toFixed(0) : x.toFixed(digits);
}

export function fmtTokens(x: number): string {
  return compact(x, 2);
}

export function fmtUsd(x: number): string {
  if (!Number.isFinite(x)) return 'n/a';
  const abs = Math.abs(x);
  if (abs >= 1000) return `$${compact(x, abs >= 1e6 ? 2 : 1)}`;
  if (abs >= 1) return `$${x.toFixed(2)}`;
  return `$${fmtPrice(x)}`;
}

export function fmtPct(x: number, digits = 1, signed = true): string {
  if (!Number.isFinite(x)) return 'n/a';
  const v = x * 100;
  const sign = signed ? (v > 0 ? '+' : v < 0 ? '−' : '') : v < 0 ? '−' : '';
  return `${sign}${Math.abs(v).toFixed(digits)}%`;
}

/** Log return → simple percentage change. */
export function logToPct(x: number): number {
  return Math.exp(x) - 1;
}

const SUBSCRIPT = '₀₁₂₃₄₅₆₇₈₉';

/** Tiny prices in the DEX-style zero-count notation: 0.0₇217. */
export function fmtPrice(x: number): string {
  if (!Number.isFinite(x) || x <= 0) return '0';
  if (x >= 0.01) return x.toPrecision(4);
  const exp = Math.floor(Math.log10(x));
  const zeros = -exp - 1;
  const digits = Math.round(x * 10 ** (-exp + 2)).toString();
  if (zeros < 4) return x.toFixed(zeros + 3);
  const sub = zeros
    .toString()
    .split('')
    .map((d) => SUBSCRIPT[Number(d)])
    .join('');
  return `0.0${sub}${digits}`;
}

export function fmtDuration(ms: number): string {
  const s = Math.max(0, Math.round(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 48) return `${h}h ${m % 60}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

export function shortAddr(addr: string): string {
  return addr.length > 12 ? `${addr.slice(0, 6)}…${addr.slice(-4)}` : addr;
}
